import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'

const AdminPayment = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [payments, setPayments] = useState([])
  const [userRole, setUserRole] = useState(null)
  const [selectedPayment, setSelectedPayment] = useState(null)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPayments, setTotalPayments] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const itemsPerPageOptions = [10, 25, 50, 100]

  // ตรวจสอบสิทธิ์ admin
  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        navigate('/')
        return
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('membership')
          .eq('id', user.id)
          .single()

        if (error) throw error

        if (data?.membership !== 'admin') {
          navigate('/')
          return
        }

        setUserRole(data.membership)
      } catch (error) {
        console.error('Error checking admin role:', error)
        navigate('/')
      }
    }

    checkAdminRole()
  }, [user, navigate])

  // ดึงข้อมูลการชำระเงิน
  const fetchPayments = async () => {
    if (userRole !== 'admin') return

    try {
      setLoading(true)
      
      // Calculate pagination range
      const from = (currentPage - 1) * itemsPerPage
      const to = from + itemsPerPage - 1

      // ดึงข้อมูลการชำระเงิน
      const { data: paymentsData, error: paymentsError, count } = await supabase
        .from('payments')
        .select('*', { count: 'exact' })
        .range(from, to)
        .order('created_at', { ascending: false })

      if (paymentsError) throw paymentsError

      // ดึงข้อมูลโปรไฟล์
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, display_name, expires_at')

      if (profilesError) throw profilesError

      // รวมข้อมูลทั้งหมด
      const combinedData = paymentsData.map(payment => ({
        ...payment,
        profile: profilesData.find(profile => profile.id === payment.user_id)
      }))
      
      setPayments(combinedData)
      setTotalPayments(count || 0)
    } catch (error) {
      console.error('Error fetching payments:', error)
    } finally {
      setLoading(false)
    }
  }

  // Effect for fetching payments
  useEffect(() => {
    fetchPayments()
  }, [userRole, currentPage, itemsPerPage])

  // อัพเดทสถานะการชำระเงิน
  const updatePaymentStatus = async (paymentId, newStatus) => {
    try {
      // 1. ดึงข้อมูลการชำระเงิน
      const { data: paymentData, error: fetchError } = await supabase
        .from('payments')
        .select('id, user_id, package_id, payment_status')
        .eq('id', paymentId)
        .single();

      if (fetchError) throw fetchError;
      console.log('Payment Data:', paymentData);

      // 2. อัพเดทสถานะการชำระเงิน
      const { error: updateError } = await supabase
        .from('payments')
        .update({ payment_status: newStatus })
        .eq('id', paymentId);

      if (updateError) throw updateError;

      // 3. ถ้าอนุมัติ ให้อัพเดทสถานะสมาชิก
      if (newStatus === 'approved') {
        try {
          // ดึงข้อมูลแพ็กเกจ
          const { data: packageData, error: packageError } = await supabase
            .from('packages')
            .select('name')
            .eq('id', paymentData.package_id)
            .single();

          if (packageError) throw packageError;
          console.log('Package Data:', packageData);

          let membershipType = 'vip'; // ค่าเริ่มต้นเป็น vip
          let expiryDate = new Date();

          // กำหนดประเภทสมาชิกและวันหมดอายุ
          if (packageData.name.toLowerCase().includes('ตลอดชีพ')) {
            membershipType = 'lifetime';
            expiryDate = new Date(9999, 11, 31);
          } else if (packageData.name.toLowerCase().includes('1 ปี')) {
            expiryDate.setFullYear(expiryDate.getFullYear() + 1);
          } else {
            expiryDate.setMonth(expiryDate.getMonth() + 1);
          }

          // สร้าง SQL query สำหรับอัพเดทโปรไฟล์
          const { error: profileError } = await supabase.rpc(
            'update_member_profile',
            {
              user_id: paymentData.user_id,
              new_membership: membershipType,
              new_expires_at: expiryDate.toISOString()
            }
          );

          if (profileError) throw profileError;

          console.log('Profile updated successfully');

        } catch (innerError) {
          console.error('Inner Error:', innerError);
          throw innerError;
        }
      }

      // Refresh payments list
      fetchPayments()
      alert('อัพเดทสถานะการชำระเงินเรียบร้อย')
    } catch (error) {
      console.error('Error updating payment status:', error)
      alert('เกิดข้อผิดพลาดในการอัพเดทสถานะ')
    }
  }

  // เพิ่มฟังก์ชันสำหรับดูสลิป
  const viewSlip = (payment) => {
    setSelectedPayment(payment);
  };

  if (!userRole || loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">กำลังโหลด...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>จัดการการชำระเงิน - AI Unlocked</title>
      </Helmet>

      <main className="max-w-6xl mx-auto p-6">
        <div className="bg-gray-900 rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-white">จัดการการชำระเงิน</h1>
            
            {/* Items per page selector */}
            <div className="flex items-center gap-2">
              <span className="text-gray-400">แสดง:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="bg-gray-700 text-white px-3 py-1 rounded-lg"
              >
                {itemsPerPageOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="text-gray-400">รายการต่อหน้า</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    วันที่
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    อีเมล
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    จำนวนเงิน
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    วันหมดอายุ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    สถานะ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    การจัดการ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    สลิป
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {new Date(payment.created_at).toLocaleDateString('th-TH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {payment.profile?.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {payment.amount}฿
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {payment.profile?.expires_at 
                        ? new Date(payment.profile.expires_at).toLocaleDateString('th-TH', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        payment.payment_status === 'approved' 
                          ? 'bg-green-900 text-green-300'
                          : payment.payment_status === 'rejected'
                          ? 'bg-red-900 text-red-300'
                          : 'bg-yellow-900 text-yellow-300'
                      }`}>
                        {payment.payment_status === 'approved' 
                          ? 'ยืนยันแล้ว' 
                          : payment.payment_status === 'rejected'
                          ? 'ปฏิเสธแล้ว'
                          : 'รอตรวจสอบ'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {payment.payment_status === 'pending' && (
                        <>
                          <button
                            onClick={() => updatePaymentStatus(payment.id, 'approved')}
                            className="text-green-400 hover:text-green-300 mr-2"
                          >
                            ยืนยันการชำระเงิน
                          </button>
                          <button
                            onClick={() => updatePaymentStatus(payment.id, 'rejected')}
                            className="text-red-400 hover:text-red-300 mr-2"
                          >
                            ปฏิเสธการชำระเงิน
                          </button>
                        </>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      {payment.slip_image && (
                        <a
                          href={supabase.storage
                            .from('payment_slips')
                            .getPublicUrl(payment.slip_image)
                            .data.publicUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(supabase.storage
                              .from('payment_slips')
                              .getPublicUrl(payment.slip_image)
                              .data.publicUrl, '_blank');
                          }}
                          className="text-blue-400 hover:underline"
                        >
                          ดูสลิป
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex justify-between items-center">
            <div className="text-sm text-gray-400">
              แสดง {((currentPage - 1) * itemsPerPage) + 1} ถึง {Math.min(currentPage * itemsPerPage, totalPayments)} จาก {totalPayments} รายการ
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 hover:bg-gray-600"
              >
                ก่อนหน้า
              </button>
              {/* Page numbers */}
              <div className="flex gap-1">
                {[...Array(Math.min(5, Math.ceil(totalPayments / itemsPerPage)))].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 rounded-lg ${
                        currentPage === pageNum
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
                {Math.ceil(totalPayments / itemsPerPage) > 5 && (
                  <span className="px-4 py-2 text-gray-400">...</span>
                )}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, Math.ceil(totalPayments / itemsPerPage)))}
                disabled={currentPage >= Math.ceil(totalPayments / itemsPerPage)}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 hover:bg-gray-600"
              >
                ถัดไป
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default AdminPayment
